import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Tooltip as MuiTooltip, Modal, Box, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import TicketRow from '../../../../components/TicketRow';
import { reportStyles } from '../../../../shared/globalSX';
import { COLORS } from '../../../../shared/colors';
import OverflowTooltip from '../../../../shared/OverflowTooltip';

const AtRiskTickets = ({ sprintId, apiData }) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);

  let tickets = apiData?.data?.risk_tickets || [];

  // Sort by due date (closest first)
  tickets = [...tickets].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  const mappedTickets = tickets.map((ticket) => {
    const assignees = ticket.assignee || [];

    let assigneeNames = t('sprintManagement.unassigned');
    if (assignees.length > 0) {
      let displayed = [];
      let charCount = 0;
      const MAX_CHARS = 25;
      
      for (let i = 0; i < assignees.length; i++) {
        if (charCount + assignees[i].length > MAX_CHARS && i > 0) {
          break;
        }
        displayed.push(assignees[i]);
        charCount += assignees[i].length + 2;
      }
      
      const remaining = assignees.length - displayed.length;
      assigneeNames = displayed.join(', ');
      if (remaining > 0) {
        assigneeNames += ` +${remaining}`;
      }
    }

    return {
      ...ticket,
      ticket_number_prefix: ticket.project_code || '',
      ticket_number: ticket.ticket_number || '',
      title: ticket.title,
      assignees: assignees,
      assignee_name: assigneeNames,
      due_date: ticket.due_date,
      status: ticket.status,
      priority: ticket.priority
    };
  });

  // Determine how many to show in the small card (e.g., top 4)
  const previewTickets = mappedTickets.slice(0, 4);

  const getTicketLabel = (ticket) => [ticket.ticket_number_prefix, ticket.ticket_number]
    .filter((value) => value !== '' && value != null)
    .join('-');

  const handleTicketClick = (ticket) => {
    const label = getTicketLabel(ticket);
    if (label) {
      window.open(`/task/${label}`, '_blank');
    }
  };

  const renderList = (data) => (
    <>
      {data.map((ticket, idx) => (
        <div 
          key={idx} 
          className="ticket-row" 
          onClick={() => handleTicketClick(ticket)}
          style={{ cursor: 'pointer' }}
        >
          {/* Left Column */}
          <div className="left-column" style={{ minWidth: 0, flex: 1 }}>
            <OverflowTooltip title={`${ticket.id} - ${ticket.title}`} className="ticket-title" tooltipWidth={400} maxLines={1}>
              {getTicketLabel(ticket)}{getTicketLabel(ticket) ? ' - ' : ''}{ticket.title}
            </OverflowTooltip>
            <span className="assignee-name">
              {ticket.assignee_name || t('sprintManagement.unassigned')}
            </span>
          </div>

          {/* Right Column */}
          <div className="right-column">
            <span className={`priority-chip ${(ticket.priority || '').toLowerCase()}`}>
              {ticket.priority || '-'}
            </span>
            <div className="due-date-group">
              {ticket.due_date && <CalendarTodayOutlinedIcon className="calendar-icon" />}
              <span className="date-text">{ticket.due_date ? new Date(ticket.due_date).toLocaleDateString() : '-'}</span>
            </div>
          </div>
        </div>
      ))}
      {data.length === 0 && (
        <div className="empty-state">
          {t('sprintManagement.noAtRiskTickets')}
        </div>
      )}
    </>
  );

  return (
    <>
      <div className="sm-card at-risk-card">
        <div className="card-header">
          <div className="title-group">
            <h2 className="card-title">{t('sprintManagement.atRiskTicketsTitle')}</h2>
            <MuiTooltip 
              title={
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span>
                    {t('sprintManagement.atRiskTooltipText1')}
                    <span style={{ color: COLORS.status.sprintCompleted }}>{t('sprintManagement.markedAsCompleted')}</span>
                    {t('sprintManagement.atRiskTooltipText2')}
                  </span>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    <li>{t('sprintManagement.atRiskTooltipBullet1')}</li>
                    <li>{t('sprintManagement.atRiskTooltipBullet2')}</li>
                  </ul>
                </div>
              }
              placement="bottom" 
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: COLORS.white,
                    color: COLORS.black,
                    boxShadow: COLORS.shadows.card,
                    fontSize: '13px',
                    border: `1px solid ${COLORS.gray[200]}`,
                    padding: '12px 16px'
                  }
                },
                arrow: {
                  sx: {
                    color: COLORS.white
                  }
                }
              }}
            >
              <IconButton size="small" className="info-icon">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
          </div>
        </div>
        
        <div className="list-container">
          {tickets.length === 0 ? (
            <div className="empty-risk-tickets">
              <InfoOutlinedIcon className="empty-icon" />
              <span className="empty-text">
                {t('sprintManagement.noAtRiskTicketsMsg', { defaultValue: 'There are currently no at-risk tickets for this sprint.' })}
              </span>
            </div>
          ) : (
            renderList(previewTickets)
          )}
        </div>
        
        {tickets.length > previewTickets.length && (
          <div className="view-all-container">
            <span 
              onClick={() => setModalOpen(true)}
              className="view-all-btn"
            >
              {t('sprintManagement.viewAllCount', { count: tickets.length })}
            </span>
          </div>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box sx={reportStyles.ticketsModalBox}>
          <Box sx={reportStyles.ticketsModalHeader}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('sprintManagement.atRiskTicketsTitle')}</Typography>
            <IconButton onClick={() => setModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={reportStyles.ticketsModalContent}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '40%', fontSize: '15px' }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '20%', fontSize: '15px' }}>Assignee</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '15%', fontSize: '15px' }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '12.5%', fontSize: '15px' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '12.5%', fontSize: '15px' }}>Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappedTickets.map((ticket) => (
                    <TicketRow key={`${ticket.ticket_number_prefix}-${ticket.ticket_number}`} ticket={ticket} showDueDate={true} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default AtRiskTickets;
